import * as React from "react";
import { Modal, notification } from "antd";
import { useBlocker, useNavigate } from "react-router-dom";
import { parentLink, request, resourceLink } from "../../../services/cpln";
import { Advanced } from "./advanced";
import { Basic } from "./basic";
import { domainToPatch, inputToDomain } from "./utils/utils";
import { DomainInput } from "../../../schema/types/domain/input";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { hasNestedPath, useCpFormAnyValueId, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { useAppDispatch } from "../../../store/hooks";
import { setTitleName } from "../../../store/titleSlice";
import { domainApi } from "../../../store/api/domainApi";
import { DomainDraftNav } from "./nav";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { sentryLog } from "../../../tools/sentry";
import { CpFormSpace } from "../../../components/antd/form/CpFormSpace";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";

interface Props {
  formId: string;
}
export const DomainDraftContent: React.FC<Props> = ({ formId }) => {
  const basePath = useBasePath(`/domain/-draft/*`);

  const navigate = useNavigate();

  const draftType = useCpFormAnyValueId(formId, `values.meta.draftType`);
  const mode = useCpFormAnyValueId(formId, `values.meta.mode`);
  const basicName = useCpFormAnyValueId(formId, `values.basic.name`);
  const advancedName = useCpFormAnyValueId(formId, `values.advanced.name`);
  const domainName = mode === "basic" ? basicName : advancedName;

  const dispatch = useAppDispatch();

  // Set the document title name for draft pages
  React.useEffect(() => {
    if (domainName) {
      dispatch(setTitleName(domainName));
    }
    return () => {
      dispatch(setTitleName(null));
    };
  }, [domainName]);

  React.useEffect(() => {
    if (draftType === "edit") {
      return;
    }
    const startingPage = `${basePath}/content/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;
    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/domain/-create`) &&
      !nextLocation.pathname.includes(`/domain/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-draft",
        res: String(res),
      });
    }

    return res;
  });

  function scroller({ errors }) {
    // Check for root error first - scroll to top
    if (hasNestedPath(errors, "root")) {
      const container = document.querySelector(".cpln-cards-container");
      if (container) {
        container.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    const cards: Record<string, string[]> = {
      name: [`advanced.name`],
      general: [
        `basic.name`,
        `basic.description`,
        `basic.workload`,
        `advanced.spec.dnsMode`,
        `advanced.spec.certChallengeType`,
        `advanced.spec.routingMode`,
        `advanced.spec.acceptAll`,
      ],
      gvc: ["gvc"],
      workload: ["advanced.spec.workload"],
      ports: ["advanced.spec.ports"],
      tags: ["tags"],
    };

    for (const cardId of Object.keys(cards)) {
      const propertyNames = cards[cardId];
      for (const propertyName of propertyNames) {
        if (hasNestedPath(errors, propertyName)) {
          const el = document.querySelector(`div[data-section-id="${cardId}"]`);
          if (!el) {
            console.error(
              `Submit error tried to scroll ${cardId} but could not find the div element | ${`div[data-section-id="${cardId}"]`}`,
            );
            return;
          }

          el.scrollIntoView({ block: "nearest" });
          return;
        }
      }
    }
  }

  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit, scroller: scroller });

  async function handleSubmit(input: DomainInput) {
    try {
      const org = store.getState().context.org;
      const gvcName = parseLink(input.gvc.link, { useInputCtx: true }).name;
      const ctx = { org: org, gvc: gvcName };
      const draftType = input.meta.draftType;
      let body = inputToDomain(input);
      if (draftType === "edit") {
        body = domainToPatch(body, { id: input.id, version: input.version });
      }
      const method = draftType === "edit" ? "patch" : "post";
      const url = draftType === "edit" ? input.meta.domainLink : parentLink("domain", ctx);

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("domain", body.name, ctx);
      dispatch(domainApi.util.invalidateTags([{ type: "Domain", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: draftType === "edit" ? "Domain is updated" : "Domain is created",
      });
      navigate(`/console${itemLink}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. Please close this form and edit the domain again to get the latest data.",
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;

      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  return (
    <>
      <CpFormContext.Provider value={{ submit: submit, id: formId }}>
        <BasePathContext.Provider value={basePath}>
          <ContentLayout
            nav={<DomainDraftNav ignoreBlockerRef={ignoreBlockerRef} submit={submit} />}
            content={
              <div className="cpln-cards-container flex flex-col gap-4 relative">
                <CpRootError />
                <CpFormValidator validationFn={validator} />
                <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
                {mode === "basic" ? ( //
                  <Basic />
                ) : mode === "advanced" ? (
                  <Advanced />
                ) : null}
                <CpFormSpace />
                <CpPreviewModal
                  inputToItemFn={inputToDomain}
                  kind="domain"
                  namePath={mode === "basic" ? "basic.name" : "advanced.name"}
                  title="Domain"
                />
                <CpDebugModal
                  inputToItemFn={inputToDomain}
                  kind="domain"
                  namePath={mode === "basic" ? "basic.name" : "advanced.name"}
                  title="Domain"
                />
              </div>
            }
          />
        </BasePathContext.Provider>
      </CpFormContext.Provider>
    </>
  );
};
